import React, { useState } from "react";
import api from "../../services/api";
import { UserCheck, UserPlus, ClipboardSignature, Smartphone, Mail, Lock, GraduationCap, BookOpen, School, ShieldAlert, Save, FileText } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";

export default function OfflineAdmission() {
  const [activeRole, setActiveRole] = useState("student");
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    mobileNo: "",
    password: "",
    // Metadata for visual form integrity
    gender: "Male",
    dob: "",
    admissionClass: "Class 10",
    guardianName: "",
    address: "",
    subjectSpecialization: "Science & Math",
    highestQualification: "Masters in Education",
    joiningDate: new Date().toISOString().split('T')[0]
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const generatePassword = () => {
    const randomStr = Math.random().toString(36).slice(-4).toUpperCase();
    const generated = `${formData.fullName.split(" ")[0] || "LMS"}@${randomStr}`;
    setFormData({ ...formData, password: generated });
    toast.success("Secure temporary password generated!");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters long.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        email: formData.email,
        password: formData.password,
        role: activeRole,
        full_name: formData.fullName,
        mobile_no: formData.mobileNo,
        institute_name: "Default Main Institute",
        dob: formData.dob,
        father_name: activeRole === 'student' ? formData.guardianName : null,
        address: formData.address,
        course_name: activeRole === 'student' ? formData.admissionClass : formData.subjectSpecialization,
        batch: activeRole === 'student' ? (new Date().getFullYear().toString() + "-" + (new Date().getFullYear() + 1).toString()) : null,
        blood_group: null, // Could add to form if needed
        emergency_contact: null // Could add to form if needed
      };

      await api.post("/institute/users", payload);
      
      toast.success(`Offline Registration COMPLETE! Created ${activeRole === 'student' ? 'Student' : 'Teacher'} Profile.`, {
        duration: 5000
      });

      // Print Receipt concept
      const shouldPrint = window.confirm("User successfully admitted! Do you want to download the Admission Card?");
      if (shouldPrint) {
        window.print();
      }

      // Clear form
      setFormData({
        fullName: "",
        email: "",
        mobileNo: "",
        password: "",
        gender: "Male",
        dob: "",
        admissionClass: "Class 10",
        guardianName: "",
        address: "",
        subjectSpecialization: "Science & Math",
        highestQualification: "Masters in Education",
        joiningDate: new Date().toISOString().split('T')[0]
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Offline registration failed. Email might be duplicate.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl pb-20 font-['Inter',sans-serif]">
      <Toaster position="top-right" />

      {/* Top Banner */}
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4 print:hidden">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-lg shadow-indigo-600/20">
              <ClipboardSignature size={20} />
            </span>
            Offline Registrar & Admission
          </h1>
          <p className="text-xs font-black text-slate-400 uppercase tracking-widest mt-1.5">
            Directly ingest students and teachers into system database.
          </p>
        </div>

        {/* Switcher Buttons */}
        <div className="flex bg-slate-100 dark:bg-slate-900 p-1 rounded-2xl border border-slate-200 dark:border-slate-800">
          <button
            onClick={() => setActiveRole("student")}
            className={`px-5 py-2.5 rounded-xl text-xs font-black tracking-wider uppercase flex items-center gap-2 transition-all ${
              activeRole === "student"
                ? 'bg-white dark:bg-slate-800 text-indigo-600 shadow-md dark:shadow-none'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <GraduationCap size={15} /> Student Intake
          </button>
          <button
            onClick={() => setActiveRole("teacher")}
            className={`px-5 py-2.5 rounded-xl text-xs font-black tracking-wider uppercase flex items-center gap-2 transition-all ${
              activeRole === "teacher"
                ? 'bg-white dark:bg-slate-800 text-indigo-600 shadow-md dark:shadow-none'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <UserPlus size={15} /> Teacher Recruitment
          </button>
        </div>
      </div>

      {/* Simulated Offline Sheet */}
      <div className="bg-white dark:bg-slate-950 border-4 border-slate-100 dark:border-slate-900 rounded-[2.5rem] shadow-2xl overflow-hidden relative">
        {/* Fancy Paper Header Decor */}
        <div className={`h-3 w-full ${activeRole === 'student' ? 'bg-indigo-600' : 'bg-rose-600'}`} />

        <div className="p-8 md:p-12">
          {/* Form Header */}
          <div className="flex justify-between items-start border-b-2 border-dashed border-slate-200 dark:border-slate-800 pb-8 mb-10">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className={`px-3 py-1 rounded-md text-[10px] font-black uppercase tracking-widest ${
                  activeRole === 'student' 
                    ? 'bg-indigo-50 text-indigo-600 border border-indigo-100' 
                    : 'bg-rose-50 text-rose-600 border border-rose-100'
                }`}>
                  Office Copy
                </span>
                <span className="text-[10px] font-black text-slate-400 tracking-widest uppercase flex items-center gap-1">
                  <FileText size={12} /> Form NO: #OFFLINE-{Math.floor(Math.random() * 90000 + 10000)}
                </span>
              </div>
              <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                {activeRole === 'student' ? 'Admission Record Form' : 'Staff Employment Record'}
              </h2>
              <p className="text-slate-500 text-sm mt-1">Fill the fields accurately to register official system logins and portfolios.</p>
            </div>
            <div className="h-24 w-24 border-4 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl flex items-center justify-center text-slate-300 font-black text-[10px] uppercase text-center tracking-widest leading-tight hidden md:flex shrink-0">
              Attach<br/>Photo<br/>Here
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-10">
            {/* SECTION 1: PERSONAL DETAILS */}
            <div>
              <h3 className="text-xs font-black uppercase tracking-[0.2em] text-slate-400 mb-6 flex items-center gap-2">
                <span className="h-1.5 w-1.5 bg-indigo-500 rounded-full" /> Section A: Core Biographical Information
              </h3>
              
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2">Full Legal Name *</label>
                  <div className="relative">
                    <input
                      type="text"
                      name="fullName"
                      required
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="Enter candidate first & last name"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 text-sm font-semibold outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2">Date of Birth</label>
                  <input
                    type="date"
                    name="dob"
                    value={formData.dob}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 text-sm font-semibold outline-none focus:ring-4 focus:ring-indigo-500/10"
                  />
                </div>

                <div>
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2">Gender Identification</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 text-sm font-semibold outline-none focus:ring-4"
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Non-binary / Prefer Not to Disclose</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2">Permanent Address</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Street, City, Postal Code"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 text-sm font-semibold outline-none focus:ring-4"
                  />
                </div>
              </div>
            </div>

            {/* SECTION 2: ACADEMIC / ROLE SPECIFIC */}
            <div>
              <h3 className="text-xs font-black uppercase tracking-[0.2em] text-slate-400 mb-6 flex items-center gap-2">
                <span className="h-1.5 w-1.5 bg-indigo-500 rounded-full" /> Section B: Curricular Allocation
              </h3>
              
              {activeRole === 'student' ? (
                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <label className="block text-xs font-black uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2">Target Class / Standard</label>
                    <input
                      type="text"
                      name="admissionClass"
                      value={formData.admissionClass}
                      onChange={handleChange}
                      placeholder="e.g., Class 10 Science"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 dark:bg-slate-900 text-sm font-semibold outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-black uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2">Parent / Guardian Full Name</label>
                    <input
                      type="text"
                      name="guardianName"
                      value={formData.guardianName}
                      onChange={handleChange}
                      placeholder="Father's or Mother's Name"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 dark:bg-slate-900 text-sm font-semibold outline-none"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <label className="block text-xs font-black uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2">Department / Major Subject</label>
                    <input
                      type="text"
                      name="subjectSpecialization"
                      value={formData.subjectSpecialization}
                      onChange={handleChange}
                      placeholder="e.g., Advanced Chemistry & Physics"
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 dark:bg-slate-900 text-sm font-semibold outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-black uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2">Highest Professional Degree</label>
                    <input
                      type="text"
                      name="highestQualification"
                      value={formData.highestQualification}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 dark:bg-slate-900 text-sm font-semibold outline-none"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* SECTION 3: DIGITAL CREDS */}
            <div className="bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-900 p-6 md:p-8 rounded-[2rem]">
              <h3 className="text-xs font-black uppercase tracking-[0.2em] text-slate-400 mb-6 flex items-center gap-2">
                <span className="h-1.5 w-1.5 bg-emerald-500 rounded-full animate-pulse" /> Section C: Active Database Authentication Credentials
              </h3>
              
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                <div>
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2 flex items-center gap-1.5"><Mail size={12} /> Login Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="allocated@email.com"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white dark:bg-slate-950 text-sm font-black text-slate-800 dark:text-white outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2 flex items-center gap-1.5"><Smartphone size={12} /> Mobile Number</label>
                  <input
                    type="text"
                    name="mobileNo"
                    value={formData.mobileNo}
                    onChange={handleChange}
                    placeholder="+91 98XXX XXXXX"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white dark:bg-slate-950 text-sm font-bold outline-none"
                  />
                </div>

                <div className="md:col-span-2 lg:col-span-1">
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2 flex items-center justify-between">
                    <span className="flex items-center gap-1.5"><Lock size={12} /> Security Password *</span>
                    <button 
                      type="button" 
                      onClick={generatePassword}
                      className="text-[10px] text-emerald-600 underline font-bold hover:text-emerald-700"
                    >
                      Auto-Generate
                    </button>
                  </label>
                  <input
                    type="text"
                    name="password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="SecurePass@123"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white dark:bg-slate-950 text-sm font-black tracking-wide text-slate-800 dark:text-white outline-none focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500"
                  />
                </div>
              </div>

              <div className="mt-6 flex items-start gap-3 text-xs text-slate-500 bg-amber-50 border border-amber-100 p-4 rounded-xl">
                <ShieldAlert className="text-amber-600 shrink-0" size={16} />
                <p className="font-medium">
                  <strong>Important Compliance Note</strong>: Saving this form will dynamically push these details to live system tables. An activation packet containing this email and security password should be printed and physical copy delivered securely to the recipient.
                </p>
              </div>
            </div>

            {/* Footer Action */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between pt-6 border-t-2 border-dashed border-slate-200 dark:border-slate-800 print:hidden">
              <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mb-4 sm:mb-0">Authorized Registry Signature Line: ________________</p>
              
              <button
                type="submit"
                disabled={loading}
                className={`px-8 py-4.5 rounded-2xl font-black text-sm tracking-wider uppercase shadow-xl text-white flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:transform-none ${
                  activeRole === 'student' 
                    ? 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-600/20' 
                    : 'bg-rose-600 hover:bg-rose-700 shadow-rose-600/20'
                }`}
              >
                {loading ? (
                  <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Save size={16} /> Save & Conclude Admission
                  </>
                )}
              </button>
            </div>

          </form>
        </div>
      </div>

      {/* Special Printing View Wrapper */}
      <div className="hidden print:block fixed inset-0 bg-white p-20 z-[9999] font-serif">
        <div className="border-8 border-double border-slate-950 p-8 h-full text-slate-950">
          <div className="text-center border-b-4 border-slate-950 pb-4 mb-8">
            <h1 className="text-3xl uppercase font-black tracking-widest">OFFICIAL LMS INSTITUTION</h1>
            <h2 className="text-xl uppercase font-bold mt-2">SYSTEM ADMISSION CARD</h2>
          </div>
          
          <div className="space-y-6 text-lg">
            <div className="flex justify-between border-b border-slate-400 pb-2">
              <span className="font-bold">Candidate Name:</span>
              <span>{formData.fullName}</span>
            </div>
            <div className="flex justify-between border-b border-slate-400 pb-2">
              <span className="font-bold">Assigned Role:</span>
              <span className="uppercase">{activeRole}</span>
            </div>
            <div className="flex justify-between border-b border-slate-400 pb-2">
              <span className="font-bold">Allocated Department/Class:</span>
              <span>{activeRole === 'student' ? formData.admissionClass : formData.subjectSpecialization}</span>
            </div>
            <div className="flex justify-between border-b border-slate-400 pb-2">
              <span className="font-bold">Contact Mobile:</span>
              <span>{formData.mobileNo}</span>
            </div>

            <div className="mt-12 bg-slate-100 p-6 border-2 border-slate-950 rounded-lg text-base font-mono">
              <div className="text-center font-black border-b border-slate-950 pb-2 mb-4">SYSTEM AUTHENTICATION CARD</div>
              <div className="flex justify-between mb-2">
                <span>Login Email:</span>
                <span className="font-black">{formData.email}</span>
              </div>
              <div className="flex justify-between">
                <span>Initial Password:</span>
                <span className="font-black">{formData.password}</span>
              </div>
            </div>
          </div>

          <div className="absolute bottom-20 left-20 right-20 flex justify-between text-center">
            <div className="w-48 border-t border-slate-950 pt-2 font-bold">Registrar Officer</div>
            <div className="w-48 border-t border-slate-950 pt-2 font-bold">Authorized Stamp</div>
          </div>
        </div>
      </div>

    </div>
  );
}
